import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #isOpen = false;

  #updatePosition = async () => {
    // Only update position when panel is visible
    const parent = this.closest('details');
    if (!parent?.hasAttribute('open')) {
      this.#isOpen = false;
      return;
    }

    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      this.style.top = OFFSET + 'px';

      if (rect.right > viewportWidth) {
        const overflowAmount = rect.right - viewportWidth + OFFSET;
        this.style.left = `-${overflowAmount}px`;
      } else if (rect.left < 0) {
        const overflowAmount = Math.abs(rect.left) + OFFSET;
        this.style.left = `${overflowAmount}px`;
      }
    });

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver((mutations) => {
    // Only update if relevant attributes changed
    const hasRelevantChange = mutations.some(
      (m) => m.type === 'attributes' && (m.attributeName === 'style' || m.attributeName === 'class')
    );
    if (hasRelevantChange && this.#isOpen) {
      this.#updatePosition();
    }
  });

  #detailsToggleListener = (e) => {
    this.#isOpen = e.target.hasAttribute('open');
    if (this.#isOpen) {
      this.#updatePosition();
    }
  };

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
    } else if (this.#isOpen) {
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    const parent = this.closest('details');
    if (parent) {
      parent.addEventListener('toggle', this.#detailsToggleListener);
    }

    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    const parent = this.closest('details');
    if (parent) {
      parent.removeEventListener('toggle', this.#detailsToggleListener);
    }

    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
